/**
 * STMNT ↔ STATEMENT Text Animation
 * =================================
 * Animates the word "STMNT" expanding to "STATEMENT" and back.
 * Used on the "Make a STMNT" section heading.
 *
 * How it works:
 * - Finds headings containing "STMNT" within image-with-text sections
 * - Wraps each letter in a <span> for individual animation control
 * - Hidden letters (A, T, E, E) slide in from left with staggered timing
 * - Visible letters (M, N, T) shift right to accommodate new letters
 * - CSS transitions handle all movement (no JS animation loop)
 * - Loops indefinitely: STMNT → STATEMENT → STMNT
 * - Pauses when off-screen via IntersectionObserver
 *
 * Dependencies: stmnt-custom.css (.stmnt-anim styles)
 */
(function () {
  'use strict';

  var TOGGLE_INTERVAL = 2500;
  var INITIAL_DELAY = 1000;
  var STAGGER_MS = 60;

  /* Letter configuration for STATEMENT
     - char: the letter
     - hidden: true = starts hidden (inserted during expand)
     - offset: ch units visible letters shift left when collapsed
     - expandOrder/collapseOrder: stagger sequence (-1 = static) */
  var LETTERS = [
    { char: 'S', hidden: false, offset: 0, expandOrder: -1, collapseOrder: -1 },
    { char: 'T', hidden: false, offset: 0, expandOrder: -1, collapseOrder: -1 },
    { char: 'A', hidden: true, offset: 0, expandOrder: 6, collapseOrder: 0 },
    { char: 'T', hidden: true, offset: 0, expandOrder: 5, collapseOrder: 1 },
    { char: 'E', hidden: true, offset: 0, expandOrder: 4, collapseOrder: 2 },
    { char: 'M', hidden: false, offset: -3, expandOrder: 3, collapseOrder: 3 },
    { char: 'E', hidden: true, offset: 0, expandOrder: 2, collapseOrder: 4 },
    { char: 'N', hidden: false, offset: -4, expandOrder: 1, collapseOrder: 5 },
    { char: 'T', hidden: false, offset: -4, expandOrder: 0, collapseOrder: 6 },
  ];

  function initAnimation(heading) {
    var fullText = heading.textContent.trim();
    var stmntIndex = fullText.indexOf('STMNT');
    if (stmntIndex === -1) return;

    var prefix = fullText.substring(0, stmntIndex);
    var suffix = fullText.substring(stmntIndex + 5);

    heading.classList.add('stmnt-anim');
    heading.textContent = '';

    /* --- Build DOM --- */
    var wrap = document.createElement('span');
    wrap.className = 'stmnt-anim__wrap';

    if (prefix) {
      var prefixSpan = document.createElement('span');
      prefixSpan.className = 'stmnt-anim__prefix';
      prefixSpan.textContent = prefix;
      wrap.appendChild(prefixSpan);
    }

    var wordWrap = document.createElement('span');
    wordWrap.className = 'stmnt-anim__word';

    var letterSpans = [];
    for (var i = 0; i < LETTERS.length; i++) {
      var span = document.createElement('span');
      span.className = 'stmnt-anim__letter';
      span.textContent = LETTERS[i].char;
      letterSpans.push(span);
      wordWrap.appendChild(span);
    }

    wrap.appendChild(wordWrap);

    if (suffix) {
      var suffixSpan = document.createElement('span');
      suffixSpan.className = 'stmnt-anim__suffix';
      suffixSpan.textContent = suffix;
      wrap.appendChild(suffixSpan);
    }

    heading.appendChild(wrap);

    /* --- Stagger delay helper --- */
    function setDelays(direction) {
      for (var j = 0; j < letterSpans.length; j++) {
        var order =
          direction === 'expand'
            ? LETTERS[j].expandOrder
            : LETTERS[j].collapseOrder;
        if (order >= 0) {
          var d = order * STAGGER_MS + 'ms';
          letterSpans[j].style.transitionDelay = d + ', ' + d;
        } else {
          letterSpans[j].style.transitionDelay = '0ms, 0ms';
        }
      }
    }

    function collapse() {
      setDelays('collapse');
      heading.classList.remove('is-expanded', 'is-expanding');
      heading.classList.add('is-collapsing');
      for (var j = 0; j < letterSpans.length; j++) {
        if (LETTERS[j].hidden) letterSpans[j].classList.add('is-hidden');
        if (!LETTERS[j].hidden && LETTERS[j].offset !== 0) {
          letterSpans[j].style.translate = LETTERS[j].offset + 'ch';
        }
      }
    }

    function expand() {
      setDelays('expand');
      heading.classList.add('is-expanded');
      heading.classList.remove('is-collapsing');
      heading.classList.add('is-expanding');
      for (var j = 0; j < letterSpans.length; j++) {
        letterSpans[j].style.translate = '';
        letterSpans[j].classList.remove('is-hidden');
      }
    }

    /* --- Initial collapsed state (no transitions) --- */
    for (var k = 0; k < letterSpans.length; k++) {
      letterSpans[k].style.transition = 'none';
    }
    collapse();
    heading.classList.remove('is-collapsing');

    /* Enable transitions after initial state is painted */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        for (var r = 0; r < letterSpans.length; r++) {
          letterSpans[r].style.transition = '';
        }
      });
    });

    /* --- Resize: re-snap to current state without transitions --- */
    var resizeTimer;
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        for (var r = 0; r < letterSpans.length; r++) {
          letterSpans[r].style.transition = 'none';
        }
        heading.classList.remove('is-collapsing', 'is-expanding');

        if (isExpanded) {
          expand();
        } else {
          collapse();
        }
        heading.classList.remove('is-collapsing', 'is-expanding');

        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            for (var t = 0; t < letterSpans.length; t++) {
              letterSpans[t].style.transition = '';
            }
          });
        });
      }, 200);
    }
    window.addEventListener('resize', onResize);

    /* --- Toggle loop with IntersectionObserver pause/resume --- */
    var isExpanded = false;
    var intervalId = null;

    function toggle() {
      isExpanded = !isExpanded;
      if (isExpanded) {
        expand();
      } else {
        collapse();
      }
    }

    function startLoop() {
      if (intervalId) return;
      intervalId = setInterval(toggle, TOGGLE_INTERVAL);
    }

    function stopLoop() {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    }

    /* Start after initial delay, but only if visible */
    var isVisible = false;

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          var entry = entries[0];
          if (entry.isIntersecting) {
            if (!isVisible) {
              isVisible = true;
              startLoop();
            }
          } else {
            isVisible = false;
            stopLoop();
          }
        },
        { threshold: 0.1 }
      );

      setTimeout(function () {
        observer.observe(heading);
      }, INITIAL_DELAY);
    } else {
      /* Fallback: no IntersectionObserver, just run the loop */
      setTimeout(startLoop, INITIAL_DELAY);
    }
  }

  /* --- Find and initialize --- */
  function init() {
    var headings = document.querySelectorAll('.image-with-text__heading');
    for (var i = 0; i < headings.length; i++) {
      if (headings[i].textContent.indexOf('STMNT') !== -1) {
        initAnimation(headings[i]);
        break;
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
